import React from 'react';
import {
    Users as UsersIcon,
    CheckCircle2,
    XCircle,
    Shield,
    Loader2,
    ToggleRight,
    ToggleLeft
} from 'lucide-react';

export default function UsersTab({ users, usersLoading, togglingUserId, handleToggleUserStatus }) {
    return (
        <div className="space-y-6">
            {/* Stats Bar */}
            <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                            <UsersIcon size={24} className="text-indigo-600" />
                        </div>
                        <div>
                            <div className="text-2xl font-black text-slate-900">{users.length}</div>
                            <div className="text-xs text-slate-500 uppercase font-semibold">Total Users</div>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                            <CheckCircle2 size={24} className="text-green-600" />
                        </div>
                        <div>
                            <div className="text-2xl font-black text-slate-900">{users.filter(u => u.active).length}</div>
                            <div className="text-xs text-slate-500 uppercase font-semibold">Active</div>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                            <XCircle size={24} className="text-red-600" />
                        </div>
                        <div>
                            <div className="text-2xl font-black text-slate-900">{users.filter(u => !u.active).length}</div>
                            <div className="text-xs text-slate-500 uppercase font-semibold">Inactive</div>
                        </div>
                    </div>
                </div>
                
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
                    <h3 className="font-bold text-slate-900 text-lg">User Management</h3>
                    <p className="text-sm text-slate-500 mt-1">Manage user access and permissions for this branch</p>
                </div>

                {usersLoading ? (
                    <div className="p-12 text-center">
                        <Loader2 className="animate-spin text-indigo-500 mx-auto mb-3" size={32} />
                        <p className="text-sm text-slate-500">Loading users...</p>
                    </div>
                ) : users.length === 0 ? (
                    <div className="p-16 text-center">
                        <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <UsersIcon size={40} className="text-slate-300" />
                        </div>
                        <p className="text-lg font-bold text-slate-900 mb-2">No users assigned</p>
                        <p className="text-sm text-slate-500">No users are currently assigned to this branch</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-4 text-left">
                                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">User</div>
                                    </th>
                                    <th className="px-6 py-4 text-left">
                                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Role</div>
                                    </th>
                                    <th className="px-6 py-4 text-center">
                                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Status</div>
                                    </th>
                                    <th className="px-6 py-4 text-center">
                                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Actions</div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {users.map(user => (
                                    <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-blue-200">
                                                    {user.username.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-slate-900 text-base">{user.username}</p>
                                                    <p className="text-sm text-slate-500">{user.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold">
                                                <Shield size={14} />
                                                {user.userRole || user.userType || 'User'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold ${
                                                user.active
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-red-100 text-red-700'
                                            }`}>
                                                <div className={`w-2 h-2 rounded-full ${user.active ? 'bg-green-500' : 'bg-red-500'}`} />
                                                {user.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => handleToggleUserStatus(user)}
                                                    disabled={togglingUserId === user.id}
                                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all shadow-sm ${
                                                        user.active
                                                            ? 'bg-red-500 hover:bg-red-600 text-white shadow-red-200'
                                                            : 'bg-green-500 hover:bg-green-600 text-white shadow-green-200'
                                                    } ${togglingUserId === user.id ? 'opacity-50' : ''}`}
                                                >
                                                    {togglingUserId === user.id ? (
                                                        <Loader2 size={14} className="animate-spin" />
                                                    ) : user.active ? (
                                                        <ToggleRight size={16} />
                                                    ) : (
                                                        <ToggleLeft size={16} />
                                                    )}
                                                    {user.active ? 'Deactivate' : 'Activate'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}